import { Case } from '../../features/arende'
import { DataType } from './filterconfig'
import { KIND_LABEL, reasonLabel } from '../../features/arende/components/handleFlag/reasons'
import TruncatedText from '../truncatedText/TruncatedText'
import shortenText from '../../features/arendeFordelning/components/tableUtils'

// Antd
import type { ColumnsType } from 'antd/es/table'
import { Popover, Space, Table, Tooltip } from 'antd'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import '../../styles/flagLink.css'

// Sorterings- och filterordning: det som avslutats direkt först, uppföljningar sist.
const KIND_ORDER = ['hanterad', 'avfärdad', 'uppföljning']

export const createCaseTableConfig = (data: Case[], navigate: ReturnType<typeof useHistory>, onDetaljer: (c: Case) => void) => {
  const columns: ColumnsType<Case> = [
    {
      title: 'ID',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      showSorterTooltip: { title: 'Sortera på hanteringens id' },
      filters: Array.from(
        new Set(
          data.map((d) => {
            return d.key
          })
        )
      ).map((name) => {
        return {
          value: name,
          text: name,
        }
      }),
      sorter: (a, b) => a.id - b.id,
      render: (_, obj) => <>{obj.key}</>,
      onFilter: (value, record) => record.key === value,
      filterSearch: true,
    },
    {
      title: 'Handling',
      dataIndex: 'kind',
      key: 'kind',
      width: 130,
      showSorterTooltip: { title: 'Sortera på typ av hantering' },
      filters: KIND_ORDER.map((k) => ({ value: k, text: KIND_LABEL[k] })),
      onFilter: (value, record) => record.kind === value,
      sorter: (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind),
      render: (_, obj) => <>{KIND_LABEL[obj.kind] ?? obj.kind}</>,
    },
    {
      // Etiketterna slås upp ur reasons.ts — databasen lagrar stabila nycklar, så
      // formuleringarna kan ändras utan att historiken märks om.
      title: 'Skäl',
      dataIndex: 'sub_reasons',
      key: 'sub_reasons',
      width: 240,
      showSorterTooltip: { title: 'Sortera på skäl' },
      // Sorterar på första skälets ETIKETT, inte nyckeln — annars sorteras listan efter
      // interna namn som användaren aldrig ser.
      sorter: (a, b) => {
        const först = (c: Case) => (c.sub_reasons?.length ? reasonLabel(c.sub_reasons[0]) : '')
        return först(a).localeCompare(först(b))
      },
      render: (_, obj) => {
        const skäl = (obj.sub_reasons ?? []).map(reasonLabel)
        if (skäl.length === 0) return <>—</>
        if (skäl.length === 1) return <TruncatedText text={skäl[0]} />

        // Samma mönster som utförar-kolumnen i flagglistorna: första skälet plus en
        // räknare, och klick öppnar en panel med alla. Interaktivt innehåll hör hemma
        // i en popover, inte i en hover-tooltip.
        return (
          <Popover
            content={
              <>
                <div style={{ fontWeight: 500, marginBottom: 6 }}>Skäl</div>
                {skäl.map((s, i) => (
                  <div key={i}>{s}</div>
                ))}
              </>
            }
            overlayClassName="flag-popover"
            placement="topLeft"
            trigger="click"
          >
            <span style={{ display: 'block', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <span>{shortenText(skäl[0], 26)}</span>
              <span style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}>+{skäl.length - 1}</span>
            </span>
          </Popover>
        )
      },
    },
    {
      // Visar om hanteringen gjordes som bulk eller på en enskild flagga.
      title: 'Antal flaggor',
      dataIndex: 'flags',
      key: 'flags',
      // 145: rubriken är 13 tecken (~94 px) plus sorteringspil och padding. Smalare än så
      // radbryts den till två rader.
      width: 145,
      showSorterTooltip: { title: 'Sortera på antal flaggor i hanteringen' },
      sorter: (a, b) => (a.flags?.length ?? 0) - (b.flags?.length ?? 0),
      render: (_, obj) => <>{obj.flags?.length ?? 0}</>,
    },
    {
      title: 'Hanterad av',
      dataIndex: 'ägare',
      key: 'ägare',
      width: 160,
      showSorterTooltip: { title: 'Sortera på vem som hanterade flaggan' },
      filters: Array.from(
        new Set(
          data.map((d) => {
            return d.ägare.namn
          })
        )
      )
        .map((name) => {
          return {
            value: name,
            text: name,
          }
        })
        .sort((a, b) => a.value.localeCompare(b.value)),
      render: (_, obj) => <TruncatedText text={obj.ägare.namn} />,
      sorter: (a, b) => a.ägare.namn.localeCompare(b.ägare.namn),
      onFilter: (value, record) => record.ägare.namn.includes(value + ''),
      filterSearch: true,
    },
    {
      title: 'Tilldelad',
      dataIndex: 'handläggare',
      key: 'handläggare',
      width: 160,
      showSorterTooltip: { title: 'Sortera på handläggare' },
      filters: Array.from(
        new Set(
          data.flatMap((d) => {
            return d.handläggare.map((h) => h.namn)
          })
        )
      )
        .map((name) => {
          return {
            value: name,
            text: name,
          }
        })
        .sort((a, b) => a.value.localeCompare(b.value)),
      // Villkoren stod tidigare i fel ordning (`length > 0 ? 'Flera'`), så EN handläggare
      // visades som "Flera". Namnen klipps nu på cellbredd med tooltip, som i flaggvyerna.
      render: (_, obj) => (obj.handläggare.length ? <TruncatedText text={obj.handläggare.map((h) => h.namn).join(', ')} /> : <>Ingen</>),
      sorter: (a, b) => b.handläggare.length - a.handläggare.length,
      onFilter: (value, record) => record.handläggare.map((h) => h.namn).includes(value + ''),
      filterSearch: true,
    },
    {
      title: 'Hanterad',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      defaultSortOrder: 'descend',
      showSorterTooltip: { title: 'Sortera på datum' },
      filters: Array.from(
        new Set(
          data.map((d) => {
            return moment(d.created).format('YYYY-MM-DD')
          })
        )
      ).map((name) => {
        return {
          value: name,
          text: name,
        }
      }),
      sorter: (a, b) => a.created.getTime() - b.created.getTime(),
      render: (_, obj) => <>{moment(obj.created).format('YYYY-MM-DD')}</>,
      onFilter: (value, record) => moment(record.created).format('YYYY-MM-DD') === value,
      filterSearch: true,
    },
    // "Senast uppdaterad" och den expanderbara raden är borttagna. Raden upprepade fyra
    // kolumner som redan står i tabellen; det enda unika var Senast uppdaterad, och
    // detaljvyns tidslinje visar hela händelsehistoriken i stället för ett datum.
    {
      dataIndex: 'uppdatera',
      key: 'uppdatera',
      fixed: 'right',
      width: 110,
      // Olika åtgärd per rad. Avfärdad och hanterad är avslutade konstateranden — det
      // finns ingenting att uppdatera, bara att läsa och eventuellt ångra, och det
      // ryms i en modal utan att man lämnar listan. Uppföljningar har en riktig
      // livscykel (anteckningar, stängning, tidslinje) och behåller sin sida.
      render: (_, record) => (
        <Space size="middle">
          {record.kind === 'uppföljning' ? (
            <button className="table-edit-link" onClick={() => navigate.push(`/app/arenden/${record.key}`)}>
              Uppdatera
            </button>
          ) : (
            <button className="table-edit-link" onClick={() => onDetaljer(record)}>
              Detaljer
            </button>
          )}
        </Space>
      ),
    },
  ]

  return columns
}

/** Summan av kolumnbredderna — Table behöver ett scroll.x för att fasta kolumner ska fungera. */
export const caseTableScrollX = (columns: ColumnsType<Case>) => columns.reduce((sum, col) => sum + ((col.width as number) || 0), 0)
